import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { AI_SEARCH } from '../../services/apollo/queries/aiSearchQueries';
import {
  setQuery,
  setResults,
  setLoading,
  setError,
  clearResults,
  selectAISearchQuery,
  selectAISearchResults,
  selectAISearchLoading,
  selectAISearchError,
} from '../../slices/aiSearchSlice';
import {
  selectEffectiveCoordinates,
  selectEffectiveCoordinatesAreReal,
} from '../../slices/locationSlice';

const RATE_LIMIT_CODE = 'AI_RATE_LIMITED';

const serializeError = (apolloError) => {
  if (!apolloError) return null;

  const graphQLError = apolloError.graphQLErrors?.[0];
  const networkError = apolloError.networkError;
  const extensions = graphQLError?.extensions || {};
  const code = extensions.code || (networkError ? 'NETWORK_ERROR' : 'UNKNOWN');

  return {
    message:
      graphQLError?.message ||
      apolloError.message ||
      networkError?.message ||
      'AI search failed',
    code,
    retryAfterMs:
      typeof extensions.retryAfterMs === 'number'
        ? extensions.retryAfterMs
        : null,
    remaining:
      typeof extensions.remaining === 'number' ? extensions.remaining : null,
  };
};

const isRateLimitError = (errorPayload) =>
  errorPayload?.code === RATE_LIMIT_CODE;

const useAISearch = () => {
  const dispatch = useDispatch();
  const reduxQuery = useSelector(selectAISearchQuery);
  const results = useSelector(selectAISearchResults);
  const loading = useSelector(selectAISearchLoading);
  const error = useSelector(selectAISearchError);
  const coordinates = useSelector(selectEffectiveCoordinates);
  const coordinatesAreReal = useSelector(selectEffectiveCoordinatesAreReal);
  const requestIdRef = useRef(0);

  const [runQuery] = useLazyQuery(AI_SEARCH, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
  });

  const executeSearch = useCallback(
    async (queryOrOptions) => {
      let nextQuery;
      let radiusKm;

      if (typeof queryOrOptions === 'string') {
        nextQuery = queryOrOptions;
      } else if (queryOrOptions && typeof queryOrOptions === 'object') {
        nextQuery = queryOrOptions.query;
        radiusKm = queryOrOptions.radiusKm;
      }

      const effectiveQuery = (nextQuery ?? reduxQuery ?? '').trim();

      if (!effectiveQuery) {
        const emptyError = {
          message: 'Please enter a search query.',
          code: 'EMPTY_QUERY',
          retryAfterMs: null,
          remaining: null,
        };
        dispatch(setError(emptyError));
        return { ok: false, error: emptyError, data: null };
      }

      if (effectiveQuery !== reduxQuery) {
        dispatch(setQuery(effectiveQuery));
      }

      const variables = { query: effectiveQuery };
      if (
        coordinatesAreReal &&
        Array.isArray(coordinates) &&
        coordinates.length === 2
      ) {
        const [longitude, latitude] = coordinates;
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          variables.latitude = latitude;
          variables.longitude = longitude;
        }
      }
      if (typeof radiusKm === 'number' && Number.isFinite(radiusKm)) {
        variables.radiusKm = radiusKm;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      dispatch(setLoading(true));

      try {
        const response = await runQuery({
          variables: { input: variables },
        });

        if (requestId !== requestIdRef.current) {
          return { ok: false, stale: true, data: null, error: null };
        }

        if (response?.error) {
          const payload = serializeError(response.error);
          dispatch(setError(payload));
          return { ok: false, error: payload, data: null };
        }

        const data = response?.data?.aiSearch ?? null;
        if (!data) {
          const payload = {
            message: 'AI search returned no data.',
            code: 'NO_DATA',
            retryAfterMs: null,
            remaining: null,
          };
          dispatch(setError(payload));
          return { ok: false, error: payload, data: null };
        }

        dispatch(setResults(data));
        return { ok: true, data, error: null };
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return { ok: false, stale: true, data: null, error: null };
        }
        const payload = serializeError(err);
        dispatch(setError(payload));
        return { ok: false, error: payload, data: null };
      }
    },
    [coordinates, coordinatesAreReal, dispatch, reduxQuery, runQuery]
  );

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    dispatch(clearResults());
  }, [dispatch]);

  const updateQuery = useCallback(
    (next) => {
      dispatch(setQuery(next));
    },
    [dispatch]
  );

  return {
    executeSearch,
    reset,
    setQuery: updateQuery,
    query: reduxQuery,
    results,
    loading,
    error,
    isRateLimited: isRateLimitError(error),
    retryAfterMs: error?.retryAfterMs ?? null,
  };
};

export default useAISearch;
